import React from 'react';
import { Check, X, Send } from 'lucide-react';
import { ANSWER_SHAPES } from '../../brand';
import { AnswerShapeIcon } from './AnswerShapeIcon';

interface Option {
  id: string;
  text: string;
}

interface Props {
  options: Option[];
  selectedIds: string[];
  onToggle: (optionId: string) => void;
  onSubmit: () => void;
  submitted?: boolean;
  disabled?: boolean;
  correctOptionIds?: string[];
  /** Sin botón de confirmar: en los exámenes la respuesta se guarda sola al cambiarla. */
  hideSubmit?: boolean;
}

/**
 * Selección múltiple.
 *
 * A diferencia del resto de tipos, aquí hace falta un botón de envío: el
 * jugador tiene que poder cambiar de idea antes de confirmar. El aviso de que
 * marcar de más descuenta se muestra por adelantado, porque una regla de
 * puntuación que solo se descubre al perder puntos se siente como una trampa.
 */
export const MultiSelectQuestion: React.FC<Props> = ({
  options,
  selectedIds,
  onToggle,
  onSubmit,
  submitted = false,
  disabled = false,
  correctOptionIds,
  hideSubmit = false,
}) => {
  const revealed = Array.isArray(correctOptionIds);
  const locked = disabled || submitted;

  return (
    <div className="w-full max-w-4xl mx-auto space-y-4">
      <p className="text-center text-ink-400 text-sm">
        Marca todas las que apliquen · <span className="text-ink-300">marcar de más resta</span>
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((opt, index) => {
          const shape = ANSWER_SHAPES[index % ANSWER_SHAPES.length];
          const isSelected = selectedIds.includes(opt.id);
          const isCorrect = revealed && correctOptionIds!.includes(opt.id);
          const missed = revealed && isCorrect && !isSelected;
          const wrongPick = revealed && isSelected && !isCorrect;

          let tone = isSelected
            ? `${shape.button} border-2`
            : 'bg-ink-850 hover:bg-ink-800 border-2 border-ink-700';

          if (revealed) {
            if (isCorrect) tone = 'bg-success-500/90 border-2 border-success-400';
            else if (wrongPick) tone = 'bg-danger-500/25 border-2 border-danger-500';
            else tone = 'bg-ink-850 border-2 border-ink-700 opacity-55';
          }

          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => !locked && onToggle(opt.id)}
              disabled={locked}
              aria-pressed={isSelected}
              className={`
                flex items-center gap-3 px-4 py-4 rounded-xl text-left
                transition-all duration-150 ${tone}
                ${locked ? 'cursor-default' : 'cursor-pointer hover:scale-[1.01]'}
              `}
            >
              <span
                className={`
                  w-6 h-6 rounded-md border-2 shrink-0 flex items-center justify-center
                  ${isSelected ? 'bg-white border-white' : 'border-ink-500'}
                `}
              >
                {isSelected && <Check className="w-4 h-4 text-ink-900" strokeWidth={3} />}
              </span>

              <span className={isSelected || revealed ? 'text-white/90' : 'text-ink-400'}>
                <AnswerShapeIcon shape={shape.id} className="w-5 h-5" />
              </span>

              <span className="flex-1 font-medium text-ink-50">{opt.text}</span>
              <span className="sr-only">Opción {shape.label}</span>

              {missed && (
                <span className="chip border-success-400 text-success-400 bg-success-500/10">
                  faltaba
                </span>
              )}
              {wrongPick && <X className="w-5 h-5 text-danger-400 shrink-0" />}
            </button>
          );
        })}
      </div>

      {!revealed && !hideSubmit && (
        <button
          type="button"
          onClick={onSubmit}
          disabled={locked || selectedIds.length === 0}
          className="
            w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold
            bg-ember-500 hover:bg-ember-400 text-on-accent
            disabled:bg-ink-800 disabled:text-ink-500 disabled:cursor-not-allowed
            transition-colors
          "
        >
          <Send className="w-5 h-5" />
          {submitted
            ? 'Respuesta enviada'
            : selectedIds.length === 0
              ? 'Elige al menos una'
              : `Confirmar ${selectedIds.length} ${selectedIds.length === 1 ? 'opción' : 'opciones'}`}
        </button>
      )}
    </div>
  );
};
